import {useState} from 'react';
import {CheckCircle2} from 'lucide-react';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {X} from 'lucide-react';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';
import {enrollBrandVars} from '@client/pages/public/enroll/brand';
import {
  PLAN_NOTICES,
  PLAN_NOTICES_INTRO,
} from '@client/pages/public/enroll/checkout/PlanNotices';

interface ConfirmEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Advances to the confirmation step, which runs the submission and shows its own loading state. */
  onConfirm: () => void;
}

const CERTIFICATIONS = [
  'The information you provided is complete, true, and accurate to the best of your knowledge.',
  'You understand eligibility requirements and dependent verification requirements, if applicable.',
  'You authorize applicable payroll deductions for elected benefits.',
  'You understand that making false statements or enrolling ineligible individuals may result in loss of coverage, repayment obligations, disciplinary action, or other remedies permitted by law and plan terms.',
];

export function ConfirmEnrollmentDialog({
  open,
  onOpenChange,
  onConfirm,
}: ConfirmEnrollmentDialogProps) {
  const {group} = useEnrollSteps();
  const {isPreview, isDemo} = useEnrollPreview();
  const [approved, setApproved] = useState(false);
  const [hintOpen, setHintOpen] = useState(true);

  function handleOpenChange(next: boolean) {
    if (!next) setApproved(false);
    onOpenChange(next);
  }

  function handleConfirm() {
    if (!approved) return;
    onConfirm();
    setApproved(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        style={enrollBrandVars(group.primaryColor)}
        className={`max-h-[85vh] max-w-2xl overflow-y-auto ${POSTHOG_UNMASK_CLASS}`}
      >
        <DialogHeader className='border-b border-neutral-200 pb-4'>
          <DialogTitle className='flex items-center gap-2 text-[var(--enroll-ink)]'>
            <CheckCircle2 className='h-5 w-5 text-[var(--enroll-primary)]' />
            Review &amp; confirm your enrollment
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className='text-sm leading-relaxed text-neutral-600'>
          The benefit elections you make during open enrollment will remain in effect for the
          applicable plan year unless you experience a qualifying life event permitted under the
          Internal Revenue Code, HIPAA, and the terms of the Plan.
        </DialogDescription>
        <p className='text-sm leading-relaxed text-neutral-600'>{PLAN_NOTICES_INTRO}</p>

        <div className='space-y-2'>
          <p className='text-sm font-semibold text-neutral-900'>
            By submitting your enrollment elections, you acknowledge and certify that:
          </p>
          <ul className='space-y-2'>
            {CERTIFICATIONS.map((line) => (
              <li key={line} className='flex gap-2 text-sm text-neutral-600'>
                <span aria-hidden className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400' />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <dl className='space-y-3 border-t border-neutral-200 pt-4'>
          {PLAN_NOTICES.map((notice) => (
            <div key={notice.title}>
              <dt className='text-xs font-semibold text-neutral-700'>{notice.title}</dt>
              <dd className='mt-0.5 text-xs leading-relaxed text-neutral-500'>{notice.body}</dd>
            </div>
          ))}
        </dl>

        <label className='flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700'>
          <Checkbox
            checked={approved}
            onCheckedChange={(value) => setApproved(value === true)}
            className='mt-0.5'
          />
          <span>
            I certify that the information I have provided is complete, true, and accurate, and
            authorize the applicable payroll deductions for the benefits I have elected. I understand
            these elections remain in effect for the plan year unless I experience a qualifying life
            event.
          </span>
        </label>

        <div className='flex flex-col-reverse gap-2 border-t border-neutral-200 pt-4 sm:flex-row sm:justify-end'>
          <button
            type='button'
            onClick={() => handleOpenChange(false)}
            className='inline-flex h-11 items-center justify-center rounded-lg border border-neutral-300 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100'
          >
            Go back
          </button>
          <div className='relative'>
            {isPreview && hintOpen && (
              <div className='absolute bottom-full right-0 mb-2 w-64 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-md'>
                <button
                  type='button'
                  onClick={() => setHintOpen(false)}
                  className='absolute right-1.5 top-1.5 text-amber-500 hover:text-amber-800'
                  aria-label='Dismiss'
                >
                  <X className='h-3.5 w-3.5' />
                </button>
                <span className='font-semibold'>{isDemo ? 'Demo' : 'Preview'}</span> — this won’t be
                submitted. You’ll see a sample confirmation instead.
              </div>
            )}
            <button
              type='button'
              disabled={!approved}
              onClick={handleConfirm}
              className='inline-flex h-11 items-center justify-center rounded-lg bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--enroll-accent-hover)] disabled:cursor-not-allowed disabled:bg-[color-mix(in_srgb,var(--enroll-accent)_40%,white)] disabled:shadow-none'
            >
              Agree &amp; submit enrollment
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
